import { useId, useState } from 'react'
import { useForm, useWatch, Controller, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { LinkedTransactionOption, TransactionSummary } from '@/api/transactions'
import type { AccountSummary } from '@/api/accounts'
import type { Category } from '@/api/categories'
import { AmountCalculatorInput } from '@/components/AmountCalculatorInput'
import { DatePicker } from '@/components/DatePicker'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { today } from '@/lib/transactionDateRangeFilter'
import { kindInfo } from './addKinds'
import {
  AmountHero,
  CategoryListbox,
  Field,
  FormShell,
  MoreSection,
  TransactionLinkCombobox,
} from './fields'
import {
  errorText,
  refundSchema,
  transactableAccounts,
  useCreateTransaction,
  type RefundForm as RefundValues,
} from './formLogic'

export function RefundForm({
  accounts,
  categories,
  refundTarget,
  onBack,
  onClose,
}: {
  accounts: AccountSummary[]
  categories: Category[]
  /** Opened from a row's "Record Refund": that expense is already picked. */
  refundTarget?: TransactionSummary
  onBack?: () => void
  onClose: () => void
}) {
  const formId = useId()
  const info = kindInfo('refund')
  const [refundLink, setRefundLink] = useState<LinkedTransactionOption | null>(() =>
    refundTarget
      ? {
          id: refundTarget.id,
          type: 'EXPENSE',
          description: refundTarget.description,
          date: refundTarget.date,
          amount: refundTarget.amount,
          financialAccountName: refundTarget.financialAccountName ?? null,
          categoryName: refundTarget.categoryName ?? null,
          remainingRefundableAmount: refundTarget.remainingRefundableAmount ?? null,
          payments: refundTarget.payments,
        }
      : null
  )

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<RefundValues>({
    resolver: zodResolver(refundSchema) as Resolver<RefundValues>,
    defaultValues: {
      standalone: false,
      date: today(),
      description: refundTarget ? `Refund: ${refundTarget.description}` : '',
      relatedTransactionId: refundTarget?.id ?? '',
      financialAccountId: '',
      categoryId: '',
    },
  })
  const standalone = useWatch({ control, name: 'standalone' })
  const create = useCreateTransaction(onClose)
  const { format } = useFormatCurrency()
  const [backIntoId, setBackIntoId] = useState('')
  // A multi-account expense refunds into one of its accounts; the largest part is the default.
  const parts = !standalone && (refundLink?.payments?.length ?? 0) >= 2 ? refundLink!.payments! : []
  const largest = [...parts].sort(
    (a, b) =>
      b.amount - a.amount ||
      (a.financialAccountName ?? '').localeCompare(b.financialAccountName ?? '')
  )[0]
  const backInto = parts.some((p) => p.financialAccountId === backIntoId)
    ? backIntoId
    : (largest?.financialAccountId ?? '')

  function onSubmit(values: RefundValues) {
    create.mutate({
      type: 'REFUND',
      relatedTransactionId: values.standalone ? undefined : values.relatedTransactionId,
      financialAccountId: values.standalone ? values.financialAccountId : backInto || undefined,
      categoryId: values.standalone ? values.categoryId : undefined,
      amount: values.amount,
      date: values.date,
      description: values.description,
      notes: values.notes || undefined,
    })
  }

  return (
    <FormShell
      title={info.label}
      Icon={info.Icon}
      tone={info.tone}
      onBack={onBack}
      onClose={onClose}
      formId={formId}
      submitLabel="Save Refund"
      submitting={create.isPending}
      error={create.error ? errorText(create.error) : null}
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {!standalone && (
          <Field label="Refund for" error={errors.relatedTransactionId?.message}>
            <Controller
              control={control}
              name="relatedTransactionId"
              render={() => (
                <TransactionLinkCombobox
                  value={refundLink}
                  label="expense"
                  refundableOnly
                  placeholder="Search expenses by description…"
                  onChange={(option) => {
                    setRefundLink(option)
                    setValue('relatedTransactionId', option?.id ?? '')
                    if (option) setValue('description', `Refund: ${option.description}`)
                  }}
                />
              )}
            />
          </Field>
        )}
        {parts.length > 0 && (
          <Field label="Back into">
            <Select aria-label="Refund account" value={backInto} onChange={setBackIntoId}>
              {parts.map((p) => (
                <option key={p.financialAccountId} value={p.financialAccountId}>
                  {p.financialAccountName} · {format(p.amount)}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <label className="flex items-start gap-2 text-sm text-[var(--fl-text-1)]">
          <Checkbox {...register('standalone')} />
          <span>I don&rsquo;t have the original expense (standalone refund)</span>
        </label>
        {standalone && (
          <>
            <Field label="Back into" error={errors.financialAccountId?.message}>
              <Controller
                control={control}
                name="financialAccountId"
                render={({ field }) => (
                  <Select
                    aria-label="Refund account"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    <option value="">Select account</option>
                    {transactableAccounts(accounts).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </Field>
            <Field label="Category" error={errors.categoryId?.message}>
              <Controller
                control={control}
                name="categoryId"
                render={({ field }) => (
                  <CategoryListbox
                    categories={categories}
                    type="EXPENSE"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                )}
              />
            </Field>
          </>
        )}

        <AmountHero label="Refunded" error={errors.amount?.message}>
          <Controller
            control={control}
            name="amount"
            render={({ field }) => (
              <AmountCalculatorInput
                value={field.value?.toString() ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                max={standalone ? undefined : (refundLink?.remainingRefundableAmount ?? undefined)}
                placeholder="0.00"
              />
            )}
          />
        </AmountHero>

        <Field label="Description" error={errors.description?.message}>
          <Input aria-label="Description" {...register('description')} />
        </Field>
        <Field label="Date" error={errors.date?.message}>
          <Controller
            control={control}
            name="date"
            render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
          />
        </Field>
        <MoreSection label="More — note">
          <Field label="Notes (optional)">
            <Textarea {...register('notes')} rows={2} />
          </Field>
        </MoreSection>
      </form>
    </FormShell>
  )
}
